import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeaderTypist from './HeaderTypist';
import homeData from '../data/home.json';
import '../css/home.css';
import { trackApi } from '../utils/TrackIp';
import projectData from '../data/projects.json';
import SEO from './Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChess, faBook, faArrowRight, faExternalLinkAlt, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { faGithub as faGithubBrand, faLinkedin as faLinkedinBrand, faTwitter as faTwitterBrand, faStackOverflow as faStackOverflowBrand } from '@fortawesome/free-brands-svg-icons';

function Home() {
  useEffect(() => {
    trackApi('home');
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Featured projects for homepage
  const featuredProjects = projectData.projects.filter(project => project.displayHome === true).slice(0, 3);

  // Latest blogs
  const latestBlogs = homeData.blogs.slice(0, 3);

  // Skills for homepage
  const homepageSkills = homeData.skills.slice(0, 6);

  const ProjectCard = ({ project, index }) => (
    <div
      className="col-lg-4 col-md-6 mb-4 animate-fade-in"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="card project-card h-100 glass-effect">
        <div className="card-img-wrapper">
          <img
            src={project.img || 'https://avatars.githubusercontent.com/u/9919?s=200&v=4'}
            alt={project.name}
            className="card-img-top"
            loading="lazy"
          />
          <div className="card-img-overlay d-flex align-items-end">
            <div className="card-actions">
              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm me-2"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                  View
                </a>
              )}
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-light btn-sm"
                >
                  <FontAwesomeIcon icon={faGithubBrand} className="me-1" />
                  Code
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          <h5 className="card-title text-gradient">{project.name}</h5>
          <p className="card-text text-secondary">{project.description}</p>
          <div className="project-tech">
            {project.technologies && project.technologies.slice(0, 3).map((tech, idx) => (
              <span key={idx} className="tech-badge">{tech}</span>
            ))}
            {project.technologies && project.technologies.length > 3 && (
              <span className="tech-badge">+{project.technologies.length - 3}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const BlogCard = ({ blog, index }) => (
    <div
      className="col-md-4 mb-4 animate-fade-in"
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      <div className="card blog-card h-100">
        <div className="card-body">
          <div className="blog-meta mb-3">
            <span className="blog-date">
              <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
              {blog.date}
            </span>
          </div>
          <h5 className="card-title">
            <Link to={`/blogs/${blog.slug}`} className="blog-title">
              {blog.title}
            </Link>
          </h5>
          <p className="card-text text-secondary">{blog.description}</p>
          <Link
            to={`/blogs/${blog.slug}`}
            className="btn btn-outline-primary btn-sm"
          >
            Read More <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
          </Link>
        </div>
      </div>
    </div>
  );

  const SkillCard = ({ skill, index }) => (
    <div
      className="col-lg-4 col-md-6 mb-4 animate-fade-in"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="card skill-card h-100 text-center">
        <div className="card-body">
          <div className="skill-icon mb-3">
            <div className="skill-icon-circle">
              <FontAwesomeIcon icon={skill.icon || faGithubBrand} size="2x" />
            </div>
          </div>
          <h5 className="card-title mb-3">{skill.title}</h5>
          <div className="skill-tech">
            {skill.technologies.map((tech, idx) => (
              <span key={idx} className="tech-badge">{tech}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEO page="home" />

      {/* Hero Section */}
      <section
        className="hero-section"
        role="banner"
        aria-labelledby="hero-title"
      >
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="row align-items-center min-vh-100">
              <div className="col-lg-6">
                <div className="hero-text animate-slide-up">
                  <p className="hero-greeting" id="hero-greeting">HEY THERE! 👋</p>
                  <h1 className="hero-title" id="hero-title">
                    I AM <span className="text-gradient">{homeData.name}</span>
                  </h1>
                  <div className="hero-typist mb-4" role="text" aria-live="polite">
                    <HeaderTypist texts={homeData.typistAnimationText}/>
                  </div>
                  <p className="hero-description" id="hero-description">
                    {homeData.description}
                  </p>
                  <div className="hero-cta mt-5">
                    <button
                      className="btn btn-primary btn-lg me-3"
                      onClick={() => scrollToSection('featured-projects')}
                      aria-describedby="view-work-desc"
                    >
                      View My Work
                    </button>
                    <span id="view-work-desc" className="sr-only">
                      Scroll down to see featured projects section
                    </span>
                    <button
                      className="btn btn-outline-primary btn-lg"
                      onClick={() => scrollToSection('contact')}
                      aria-describedby="contact-desc"
                    >
                      Get In Touch
                    </button>
                    <span id="contact-desc" className="sr-only">
                      Scroll down to contact section
                    </span>
                  </div>
                  <div className="hero-social mt-5" role="list" aria-label="Social media links">
                    <a
                      href={homeData.social && homeData.social.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link me-3"
                      aria-label="Visit my GitHub profile"
                      role="listitem"
                    >
                      <FontAwesomeIcon icon={faGithubBrand} size="lg" />
                    </a>
                    <a
                      href={homeData.social && homeData.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link me-3"
                      aria-label="Visit my LinkedIn profile"
                      role="listitem"
                    >
                      <FontAwesomeIcon icon={faLinkedinBrand} size="lg" />
                    </a>
                    <a
                      href={homeData.social && homeData.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link me-3"
                      aria-label="Visit my Twitter profile"
                      role="listitem"
                    >
                      <FontAwesomeIcon icon={faTwitterBrand} size="lg" />
                    </a>
                    <a
                      href={homeData.social && homeData.social.stackoverflow}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label="Visit my Stack Overflow profile"
                      role="listitem"
                    >
                      <FontAwesomeIcon icon={faStackOverflowBrand} size="lg" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="hero-image animate-fade-in">
                  <div className="hero-avatar">
                    <img
                      src="/profile.jpg"
                      alt="Professional headshot of Nikhil Bhatt, Full Stack Developer"
                      className="avatar-image"
                      loading="eager"
                    />
                    <div className="avatar-glow"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section
        id="featured-projects"
        className="section-padding"
        aria-labelledby="projects-title"
      >
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title" id="projects-title">Featured Projects</h2>
            <p className="section-subtitle">
              Here are some of my recent projects that showcase my skills and passion for development
            </p>
          </div>
          <div className="row" role="list" aria-label="Featured projects">
            {featuredProjects.map((project, index) => (
              <ProjectCard key={index} project={project} index={index} />
            ))}
          </div>
          <div className="text-center mt-4">
            <Link
              to="/projects"
              className="btn btn-outline-primary"
              aria-label="View all projects"
            >
              View All Projects <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Blogs Section */}
      <section
        className="section-padding bg-tertiary"
        aria-labelledby="blogs-title"
      >
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title" id="blogs-title">Latest Blog Posts</h2>
            <p className="section-subtitle">
              Thoughts, tutorials, and insights on web development and technology
            </p>
          </div>
          <div className="row" role="list" aria-label="Latest blog posts">
            {latestBlogs.map((blog, index) => (
              <BlogCard key={index} blog={blog} index={index} />
            ))}
          </div>
          <div className="text-center mt-4">
            <Link
              to="/blogs"
              className="btn btn-primary"
              aria-label="Read more blog posts"
            >
              Read More Posts <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section
        className="section-padding"
        aria-labelledby="skills-title"
      >
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title" id="skills-title">Skills & Technologies</h2>
            <p className="section-subtitle">
              The tools and technologies I use to bring ideas to life
            </p>
          </div>
          <div className="row" role="list" aria-label="Technical skills">
            {homepageSkills.map((skill, index) => (
              <SkillCard key={index} skill={skill} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        className="section-padding bg-tertiary"
        aria-labelledby="about-title"
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="section-title mb-4" id="about-title">Beyond Code</h2>
              <p className="mb-4">
                When I&apos;m not coding, you&apos;ll find me playing chess, reading books, solving puzzles, or exploring new technologies.
                I love to dig deep dive into tech problems
              </p>
              <div className="hobbies" role="list" aria-label="Personal interests">
                <div className="hobby-item mb-3">
                  <Link
                    to="https://www.chess.com/member/nikhil-bhatt"
                    target="_blank"
                    className="text-decoration-none"
                    aria-label="Visit my Chess.com profile"
                  >
                    <FontAwesomeIcon icon={faChess} className="me-2 text-primary" aria-hidden="true" />
                    <span>Playing Chess</span>
                  </Link>
                </div>
                <div className="hobby-item">
                  <FontAwesomeIcon icon={faBook} className="me-2 text-primary" aria-hidden="true" />
                  <span>Reading Books</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="stats-grid" role="list" aria-label="Career statistics">
                <div className="stat-item text-center">
                  <div className="stat-number" aria-label="Over 50 projects completed">50+</div>
                  <div className="stat-label">Projects</div>
                </div>
                <div className="stat-item text-center">
                  <div className="stat-number" aria-label="Over 5 years of experience">5+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
                <div className="stat-item text-center">
                  <div className="stat-number" aria-label="Over 20 blog posts written">20+</div>
                  <div className="stat-label">Blog Posts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="section-padding"
        aria-labelledby="cta-title"
      >
        <div className="container">
          <div className="cta-section text-center">
            <h2 className="section-title mb-4" id="cta-title">Let&apos;s Work Together</h2>
            <p className="section-subtitle mb-5">
              Have a project in mind or want to discuss opportunities? I&apos;d love to hear from you.
            </p>
            <div className="cta-buttons">
              <Link
                to="/contact"
                className="btn btn-primary btn-lg me-3"
                aria-label="Navigate to contact page"
              >
                Get In Touch
              </Link>
              <a
                href="/resume.pdf"
                target="_blank"
                className="btn btn-outline-primary btn-lg"
                aria-label="Download my resume (PDF)"
                rel="noopener noreferrer"
              >
                Download Resume
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
